import React from 'react';
import { Box, Container, Typography, Divider, Grid, Card, CardContent, Button, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { motion } from 'framer-motion';
import PricingPlans from '../components/PricingPlans';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const faqs = [
  {
    question: '¿Puedo cambiar de plan en cualquier momento?',
    answer: 'Sí, puedes cambiar entre los planes en cualquier momento. Si cambias a un plan superior, pagarás la diferencia prorrateada. Si cambias a un plan inferior, el cambio se aplicará al siguiente período de facturación.',
  },
  {
    question: '¿Qué pasa cuando termina mi período de prueba gratuita?',
    answer: 'Al finalizar tu período de prueba gratuita de 14 días, te solicitaremos un método de pago para continuar usando MekaSys. No realizaremos cargos automáticos sin tu consentimiento.',
  },
  {
    question: '¿Puedo cancelar mi suscripción en cualquier momento?',
    answer: 'Sí, puedes cancelar tu suscripción en cualquier momento desde la sección de facturación en tu panel de administración. Al cancelar, mantendrás el acceso hasta el final del período pagado.',
  },
  {
    question: '¿Ofrecen descuentos para talleres con múltiples sucursales?',
    answer: 'Sí, tenemos planes especiales para talleres con múltiples ubicaciones. Contacta a nuestro equipo de ventas para obtener información sobre descuentos por volumen.',
  },
  {
    question: '¿Cómo se factura la suscripción anual?',
    answer: 'Las suscripciones anuales se facturan como un pago único por adelantado. Esto te permite aprovechar el descuento por pago anual. Recibirás un recordatorio antes de la renovación.',
  },
  {
    question: '¿Puedo agregar más usuarios después de contratar?',
    answer: 'Sí, puedes agregar usuarios adicionales en cualquier momento. Cada usuario adicional tiene un costo mensual dependiendo de tu plan. Los cambios en la cantidad de usuarios se reflejarán en tu próxima factura.',
  },
];

const comparisons = [
  { feature: 'Registro de vehículos', basic: 'Ilimitado', pro: 'Ilimitado' },
  { feature: 'Usuarios', basic: 'Hasta 3', pro: 'Hasta 10' },
  { feature: 'Fichas de servicio', basic: 'Básicas', pro: 'Avanzadas' },
  { feature: 'Almacenamiento de fotos', basic: '5 GB', pro: '25 GB' },
  { feature: 'Reportes y estadísticas', basic: 'Básicos', pro: 'Avanzados y exportables' },
  { feature: 'Gestión de repuestos', basic: 'Básica', pro: 'Completa con alertas de stock' },
  { feature: 'Notificaciones a clientes', basic: 'Email', pro: 'Email y SMS' },
  { feature: 'API para integraciones', basic: 'No incluido', pro: 'Incluido' },
  { feature: 'Soporte', basic: 'Correo electrónico', pro: 'Correo electrónico y chat prioritario' },
  { feature: 'Copias de seguridad', basic: 'Semanal', pro: 'Diaria' },
  { feature: 'Personalización', basic: 'Limitada', pro: 'Avanzada' },
];

const PricingPage = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: 12,
          pb: 6,
          background: 'linear-gradient(45deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.1) 100%)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              variant="h2"
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Planes y Precios
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: '800px', mx: 'auto', mb: 4, fontWeight: 400 }}
            >
              Elige el plan que se adapte a las necesidades de tu taller mecánico
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Plans */}
      <PricingPlans />

      {/* Comparison Table */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              variant="h4"
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Comparación detallada de planes
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: '700px', mx: 'auto' }}
            >
              Compara las características incluidas en cada plan para elegir el que mejor se adapte a tus necesidades
            </Typography>
          </Box>

          <Card 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            elevation={2} 
            sx={{ 
              overflow: 'hidden',
              borderRadius: '12px'
            }}
          >
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2, px: 3 }}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight={600}>Característica</Typography>
                </Grid>
                <Grid item xs={3} sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Plan Básico</Typography>
                </Grid>
                <Grid item xs={3} sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Plan Profesional</Typography>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Box>
              {comparisons.map((item, index) => (
                <React.Fragment key={index}>
                  <Box 
                    sx={{ 
                      py: 1.5, 
                      px: 3,
                      bgcolor: index % 2 === 0 ? 'background.default' : 'background.paper'
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <Typography variant="body2">{item.feature}</Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: item.basic.includes('No') ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {item.basic}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="body2"
                          fontWeight={item.pro !== item.basic ? 600 : 400}
                          color="primary"
                        >
                          {item.pro}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  {index < comparisons.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          </Card>
        </Container>
      </Box>

      {/* FAQs */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              variant="h4"
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Preguntas frecuentes
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: '700px', mx: 'auto' }}
            >
              Respuestas a las dudas más comunes sobre nuestros planes y precios
            </Typography>
          </Box>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                }
              }
            }}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
                }}
              >
                <Accordion 
                  elevation={0} 
                  sx={{ 
                    mb: 1, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    '&:before': {
                      display: 'none',
                    },
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      bgcolor: 'background.default',
                      '&:hover': {
                        bgcolor: 'background.default',
                      }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={500}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </motion.div>

          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            sx={{ 
              mt: 6, 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'primary.light',
              color: 'white',
              borderRadius: '12px'
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              ¿Tienes más preguntas?
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Nuestro equipo está disponible para resolver todas tus dudas.
            </Typography>
            <Button 
              variant="contained" 
              color="secondary"
              size="large"
              sx={{ px: 4 }}
            >
              Contactar soporte
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;